// Asset groups: the Assets page tabs and the overview's "What you own".
// Mirrors api/internal/service/groups.go — keep the two in step.
import { PERSONAL_ITEM_CATEGORIES, FIXED_ASSET_CATEGORIES } from "../constants";

export const GROUPS = [
  { key: "investments", label: "Investments", short: "Investments", sub: "Earns a return: funds, stocks, gold, bonds, schemes, crypto", color: "#5B6B8C", note: "Funds, stocks, gold, bonds, schemes, crypto · cash at brokers is under Cash" },
  { key: "cash", label: "Cash", short: "Cash", sub: "Bank accounts, cash at brokers, money owed to you", color: "#B8925A", note: "Bank accounts, uninvested broker cash, money owed to you" },
  { key: "property", label: "Property", short: "Property", sub: "Land, flats and other fixed assets", color: "#8C8F97", note: "Valued by you · changes only when you update it" },
  { key: "personal", label: "Personal Items", short: "Personal items", sub: "Things you use", color: "#BFC2C7", note: "Valued by you · changes only when you update an item" },
  { key: "travel", label: "Travel Points", short: "Travel points", sub: "Rewards you earned", color: "#E2E3E5", note: "Points × programme rate · moves with the USD rate" },
];

export const groupMeta = (key) => GROUPS.find((g) => g.key === key);

// Cash kinds, as the server's cost basis classifies them.
const CASH_TYPES = ["bank_accounts", "loans"];
const CASH_CATEGORIES = ["Savings Account", "Cash", "Fixed Deposit", "Recurring Deposit", "Loan", "Loans"];

/** Which group an asset belongs to. */
export function groupOfAsset(asset) {
  const type = asset.asset_type || "";
  if (type === "travel_points" || asset.category === "Travel Points") return "travel";
  if (type === "personal" || (!type && PERSONAL_ITEM_CATEGORIES.includes(asset.category))) return "personal";
  if (type === "fixed" || (!type && FIXED_ASSET_CATEGORIES.includes(asset.category))) return "property";
  if (CASH_TYPES.includes(type) || CASH_CATEGORIES.includes(asset.category)) return "cash";
  return "investments";
}

/** ₹ per $ implied by a stock portfolio's own valuation. */
export function stockFxRate(asset) {
  return asset.total_value_usd > 0 ? asset.current_value / asset.total_value_usd : 0;
}

/** Uninvested USD at a stock broker, in ₹. Shown under Cash, not Investments. */
export function brokerCashINR(asset) {
  if (!(asset.cash_usd > 0)) return 0;
  return asset.cash_usd * stockFxRate(asset);
}

/** What an asset contributes to its own group's total. */
export function groupValue(asset) {
  if (groupOfAsset(asset) === "travel") return asset.total_value_inr || asset.current_value || 0;
  return (asset.current_value || 0) - brokerCashINR(asset);
}

/** Today's total per group, from the dashboard's asset list. */
export function groupTotals(assets = []) {
  const sums = {};
  assets.forEach((a) => {
    const g = groupOfAsset(a);
    sums[g] = (sums[g] || 0) + groupValue(a);
    const cash = brokerCashINR(a);
    if (cash > 0) sums.cash = (sums.cash || 0) + cash;
  });
  return sums;
}

/** Money put into market investments today: the dashboard's cost basis
 *  minus personal items, cash and broker cash. Null without cost data. */
export function investmentsPutIn(invested, assets = []) {
  if (!invested || !(invested.invested > 0)) return null;
  const broker = assets.reduce((s, a) => s + brokerCashINR(a), 0);
  return invested.invested - (invested.personal_invested || 0) - (invested.cash || 0) - broker;
}
